import {t} from '../../../../i18n';

/**
 * 处理失败记录的错误消息国际化
 */
export interface SorterPlanChuteFailedRecord {
    chuteCode: string;
    errorMessage: string;
    messageKey?: string;
    details?: string | Record<string, any>;
}

/**
 * 获取国际化的错误消息
 * @param record 失败记录
 * @returns 国际化后的错误消息
 */
export const getLocalizedErrorMessage = (record: SorterPlanChuteFailedRecord): string => {
    // 如果有messageKey，尝试使用国际化
    if (record.messageKey) {
        // 解析details（可能是JSON字符串或对象）
        let detailsObj: Record<string, any> | undefined = undefined;
        if (record.details) {
            if (typeof record.details === 'string') {
                try {
                    detailsObj = JSON.parse(record.details);
                } catch (e) {
                    // 如果解析失败，忽略
                    console.warn('Failed to parse details JSON:', record.details, e);
                }
            } else {
                detailsObj = record.details;
            }
        }

        // 先尝试使用 .message 后缀（因为有些错误消息是嵌套对象结构）
        const messageKeyWithSuffix = `${record.messageKey}.message`;
        let message = t(messageKeyWithSuffix, detailsObj);

        // 如果 .message 后缀找不到翻译（返回的是 key 本身），尝试直接翻译 messageKey
        if (message === messageKeyWithSuffix) {
            message = t(record.messageKey, detailsObj);

            // 如果直接翻译也找不到，使用原始错误消息
            if (message === record.messageKey) {
                return record.errorMessage;
            }
        }

        return String(message);
    }

    // 没有messageKey，直接返回原始错误消息
    return record.errorMessage;
};

/** 导入汇总文案所需的结构化数据（后端 messageData 字段） */
export interface SorterPlanChuteImportMessageData {
    importedCount: number;
    failedCount: number;
    skippedCount: number;
    noData: boolean;
}

/**
 * 渲染导入结果的汇总文案。
 *
 * 后端只给数字、不拼展示串，文案在这里用 i18n 出——与同一弹窗里的行级报错
 * （getLocalizedErrorMessage）同口径，避免一块 UI 两套文案来源。
 *
 * @param data 后端 messageData；缺失时（旧版本后端）由调用方回退到 response.message
 * @returns 已本地化的汇总文案
 */
export const getImportSummaryMessage = (data: SorterPlanChuteImportMessageData): string => {
    if (data.noData) {
        return String(t('wms.sorterPlan.detail.import.noData'));
    }

    const base = data.failedCount > 0
        ? t('wms.sorterPlan.detail.import.summaryFailed', {
            failed: data.failedCount,
            imported: data.importedCount,
        })
        : t('wms.sorterPlan.detail.import.summaryOk', {imported: data.importedCount});

    // 跳过提示只给行数：格口号来自用户上传的 Excel，拼进文案会让消息无限变长
    const skipped = data.skippedCount > 0
        ? t('wms.sorterPlan.detail.import.summarySkipped', {skipped: data.skippedCount})
        : '';

    return `${String(base)}${String(skipped)}`;
};

